import { t, Trans } from "@lingui/macro";
import { cn } from "@reactive-resume/utils";
import { motion } from "framer-motion";
import { ArrowLeft, BarChart3, BriefcaseBusiness, FileText, Home, UserRound } from "lucide-react";
import { Link, Outlet, useLocation } from "react-router-dom";

import { CookieSettingsLink } from "@/client/components/cookie-settings/CookieSettings";
import { usePrefersReducedMotion } from "@/client/hooks/use-prefers-reduced-motion";
import { firstRoundLegalUrl } from "@/client/utils/firstRoundAppUrl";

import { PoweredByTheResume, useIsFirstRoundBrand } from "./auth-brand";

const LOGO_CLASS =
  "inline-flex w-fit rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-4";

/**
 * Visitors who arrived from firstround.com.au see the First Round lockup, as a plain image;
 * everyone else sees theResume's, linking to this domain's homepage.
 */
const BrandLogo = ({ isFirstRound, className }: { isFirstRound: boolean; className?: string }) => {
  const image = isFirstRound ? (
    <img
      src="/brand/firstround/logo.png"
      alt="First Round"
      className={cn("h-10 w-auto object-contain", className)}
      width={182}
      height={40}
    />
  ) : (
    <img
      src="/logo/theResumeDark.png"
      alt="theResume"
      className={cn("h-10 w-auto object-contain", className)}
      width={142}
      height={40}
    />
  );

  // First Round visitors are mid-funnel (sign-up, then their feedback or the practice area), so
  // the mark is not a way out.
  return isFirstRound ? (
    <span className="inline-flex w-fit">{image}</span>
  ) : (
    <Link to="/" aria-label={t`theResume home`} className={LOGO_CLASS}>
      {image}
    </Link>
  );
};

const BACK_HOME_CLASS =
  "inline-flex min-h-11 items-center gap-2 rounded-full px-3 text-sm font-medium text-slate-500 transition-colors duration-200 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2";

/** First Round visitors get no "Back home": sign-up is the funnel, and the page has no other exit. */
const BackHome = ({ isFirstRound }: { isFirstRound: boolean }) => {
  if (isFirstRound) return null;

  return (
    <Link to="/" className={BACK_HOME_CLASS}>
      <ArrowLeft className="size-4" strokeWidth={1.8} />
      <Trans>Back home</Trans>
    </Link>
  );
};

const DocumentLines = ({ widths }: { widths: string[] }) => (
  <div className="space-y-2.5">
    {widths.map((width, index) => (
      <span
        key={`${width}-${index}`}
        className="block h-1.5 rounded-full bg-slate-200/80"
        style={{ width }}
      />
    ))}
  </div>
);

const CareerDocumentArtwork = ({ reduceMotion }: { reduceMotion: boolean }) => (
  <motion.div
    aria-hidden="true"
    className="relative h-[23rem] w-[25rem] max-w-full select-none"
    animate={reduceMotion ? undefined : { y: [0, -6, 0] }}
    transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
  >
    <div className="absolute bottom-6 left-40 h-[19rem] w-[15rem] rotate-[7deg] rounded-[1.15rem] border border-sky-100 bg-[oklch(0.985_0.008_240)] shadow-[0_18px_50px_oklch(0.48_0.08_245/0.10)]" />
    <div className="absolute bottom-3 left-24 h-[20rem] w-[16rem] rotate-[2deg] rounded-[1.15rem] border border-sky-100 bg-[oklch(0.99_0.006_240)] shadow-[0_20px_55px_oklch(0.48_0.08_245/0.11)]">
      <div className="space-y-3 p-8">
        <DocumentLines widths={["46%", "78%", "63%"]} />
      </div>
    </div>

    <div className="absolute bottom-0 left-0 h-[21rem] w-[17.5rem] -rotate-[3deg] overflow-hidden rounded-[1.2rem] border border-sky-100 bg-[oklch(0.995_0.005_240)] shadow-[0_26px_75px_oklch(0.42_0.08_245/0.16)]">
      <div className="absolute inset-y-0 left-0 flex w-12 flex-col items-center gap-5 border-r border-sky-100 bg-sky-50/80 py-7 text-sky-500">
        <Home className="size-4 fill-sky-500" strokeWidth={1.75} />
        <FileText className="size-4" strokeWidth={1.75} />
        <BriefcaseBusiness className="size-4" strokeWidth={1.75} />
        <BarChart3 className="size-4" strokeWidth={1.75} />
        <UserRound className="size-4" strokeWidth={1.75} />
      </div>

      <div className="ml-12 p-7">
        <DocumentLines widths={["56%", "35%"]} />
        <div className="mt-7 h-px bg-slate-200/80" />
        <div className="mt-7">
          <DocumentLines widths={["92%", "84%", "88%", "70%"]} />
        </div>
        <div className="mt-9 h-14 rounded-lg bg-sky-50/70" />
        <div className="mt-7">
          <DocumentLines widths={["76%", "90%", "58%"]} />
        </div>
      </div>
    </div>
  </motion.div>
);

const BrandStory = ({
  reduceMotion,
  isFirstRound,
}: {
  reduceMotion: boolean;
  isFirstRound: boolean;
}) => (
  <aside className="relative hidden min-h-[100dvh] overflow-hidden border-r border-sky-100/80 lg:block">
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0"
      style={{
        background:
          "radial-gradient(circle at 7% 88%, oklch(0.94 0.065 72 / 0.72), transparent 28%), radial-gradient(circle at 82% 32%, oklch(0.92 0.045 235 / 0.82), transparent 42%), linear-gradient(145deg, oklch(0.99 0.008 235), oklch(0.965 0.018 235))",
      }}
    />
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 opacity-45"
      style={{
        backgroundImage:
          "linear-gradient(oklch(0.78 0.025 235 / 0.18) 1px, transparent 1px), linear-gradient(90deg, oklch(0.78 0.025 235 / 0.18) 1px, transparent 1px)",
        backgroundSize: "72px 72px",
      }}
    />

    <div
      className={cn(
        "relative z-10 grid min-h-[100dvh] px-[clamp(3rem,6vw,6.5rem)] py-10",
        // The résumé artwork is theResume's story; First Round visitors came to practise
        // interviews, so that row collapses to the platform credit instead.
        isFirstRound ? "grid-rows-[auto_1fr_auto]" : "grid-rows-[auto_1fr_20rem]",
      )}
    >
      <BrandLogo isFirstRound={isFirstRound} />

      <motion.div
        className="mx-auto flex w-full max-w-xl flex-col items-center justify-center py-10 text-center"
        initial={{ opacity: 0, y: reduceMotion ? 0 : 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: reduceMotion ? 0.15 : 0.65, ease: [0.16, 1, 0.3, 1] }}
      >
        <h1
          className={cn(
            "font-noto-serif text-[clamp(3rem,4.9vw,5rem)] font-medium leading-[1.04] tracking-[-0.035em] text-slate-950 [text-wrap:balance]",
            // The First Round line is shorter; 12.5ch breaks it into four ragged lines.
            isFirstRound ? "max-w-[17ch]" : "max-w-[12.5ch]",
          )}
        >
          {isFirstRound ? (
            <Trans>Practise the interview before it counts.</Trans>
          ) : (
            <Trans>Make your experience impossible to overlook.</Trans>
          )}
        </h1>
        <span className="mt-7 h-0.5 w-10 rounded-full bg-amber-500" />
        <p className="mt-5 max-w-sm text-lg leading-relaxed text-slate-600">
          {isFirstRound ? (
            <Trans>Realistic AI mock interviews, with feedback you can act on.</Trans>
          ) : (
            <Trans>Built for your ambitions, designed around your privacy.</Trans>
          )}
        </p>
      </motion.div>

      {isFirstRound ? (
        <PoweredByTheResume className="relative self-end text-center" />
      ) : (
        <div className="relative flex items-end justify-center">
          <CareerDocumentArtwork reduceMotion={reduceMotion} />
        </div>
      )}
    </div>
  </aside>
);

const LEGAL_LINK_CLASS =
  "rounded-sm font-medium text-slate-700 underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500";

/**
 * The "by continuing" line under the sign-in and sign-up forms. First Round visitors are pointed
 * at First Round's own terms and privacy policy (on firstround.com.au in production), everyone
 * else at theResume's.
 */
const LegalNotice = ({ isFirstRound }: { isFirstRound: boolean }) => (
  <p className="relative z-10 px-6 pb-[max(1.75rem,env(safe-area-inset-bottom))] text-center text-xs leading-relaxed text-slate-500">
    {isFirstRound ? (
      <Trans>
        By continuing, you agree to the First Round{" "}
        <a
          href={firstRoundLegalUrl("/terms")}
          target="_blank"
          rel="noopener noreferrer"
          className={LEGAL_LINK_CLASS}
        >
          Terms of Service
        </a>{" "}
        and{" "}
        <a
          href={firstRoundLegalUrl("/privacy")}
          target="_blank"
          rel="noopener noreferrer"
          className={LEGAL_LINK_CLASS}
        >
          Privacy Policy
        </a>
        .
      </Trans>
    ) : (
      <Trans>
        By continuing, you agree to our{" "}
        <Link to="/terms" className={LEGAL_LINK_CLASS}>
          Terms
        </Link>{" "}
        and{" "}
        <Link to="/privacy" className={LEGAL_LINK_CLASS}>
          Privacy Policy
        </Link>
        .
      </Trans>
    )}{" "}
    · <CookieSettingsLink className={LEGAL_LINK_CLASS} />
  </p>
);

export const ClerkAuthLayout = () => {
  const reduceMotion = usePrefersReducedMotion();
  const isFirstRound = useIsFirstRoundBrand();
  const { pathname } = useLocation();
  const useWidePanel =
    pathname.endsWith("/complete-profile") || pathname.endsWith("/account-link-conflict");

  return (
    <div className="min-h-[100dvh] w-full overflow-x-hidden bg-[oklch(0.985_0.008_235)] text-slate-950 lg:grid lg:grid-cols-[minmax(0,1.08fr)_minmax(28rem,0.92fr)]">
      <BrandStory reduceMotion={reduceMotion} isFirstRound={isFirstRound} />

      <main className="relative flex min-h-[100dvh] flex-col bg-[oklch(0.995_0.004_235)]">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 82% 45% at 48% -8%, oklch(0.93 0.045 235 / 0.58), transparent 62%), radial-gradient(circle at 100% 100%, oklch(0.96 0.035 72 / 0.34), transparent 30%)",
          }}
        />

        <header className="relative z-10 flex min-h-20 items-center justify-between px-5 py-5 sm:px-8 lg:min-h-24 lg:justify-end lg:px-10">
          <BrandLogo isFirstRound={isFirstRound} className="h-7 lg:hidden" />
          <BackHome isFirstRound={isFirstRound} />
        </header>

        <div className="relative z-10 flex flex-1 items-center justify-center px-5 pb-10 pt-4 sm:px-10 lg:py-12">
          <motion.div
            initial={{ opacity: 0, y: reduceMotion ? 0 : 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: reduceMotion ? 0.15 : 0.5, ease: [0.16, 1, 0.3, 1] }}
            className={cn("w-full", useWidePanel ? "max-w-[34rem]" : "max-w-[27rem]")}
          >
            {isFirstRound && <PoweredByTheResume className="mb-6 lg:hidden" />}
            <p className="mb-8 max-w-sm text-sm leading-relaxed text-slate-500 lg:hidden">
              {isFirstRound ? (
                <Trans>Realistic AI mock interviews, with feedback you can act on.</Trans>
              ) : (
                <Trans>Built for your ambitions, designed around your privacy.</Trans>
              )}
            </p>
            <Outlet />
          </motion.div>
        </div>

        <LegalNotice isFirstRound={isFirstRound} />
      </main>
    </div>
  );
};
